import { Col, DatePicker, List, Row, Typography } from '@pankod/refine-antd';
import { IResourceComponentsProps } from '@pankod/refine-core';
import { ReportsDashboard, ReportsTable, SearchNew } from 'components';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import React, { useMemo, useState } from 'react';
import { SearchIndex } from 'services/meilisearch';
import { UNCATEGORIZED_CATEGORY_ID } from 'services/report';
import MetaData from 'utilities/metadata';
import 'components/date-picker/custom-date-picker.css';

// Enable UTC plugin
dayjs.extend(utc);

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;

// Start from beginning of current year (local time)
const yearStartingDate = dayjs().startOf('year');

export const ReportsList: React.FC<IResourceComponentsProps> = () => {
  // Local timezone range (UI level)
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    yearStartingDate.startOf('day'),
    dayjs().endOf('day'),
  ]);

  const [start, end] = dateRange;
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [categoryId, setCategoryId] = useState<string>();

  // Convert local → UTC before sending to backend
  const query = useMemo(() => {
    return {
      startDate: start.utc().toISOString(),
      endDate: end.utc().toISOString(),
      tagIds: tagIds.length > 0 ? tagIds : undefined,
      categoryId,
    };
  }, [start, end, tagIds, categoryId]);

  const disabledDate = (date: Dayjs) => date.isAfter(dayjs(), 'day');

  const onSelectOption = async (_searchText: string, ids?: string[]) => {
    setTagIds(ids || []);
  };

  const onSelectOptionCategory = async (_searchText: string, ids?: string[]) => {
    setCategoryId(ids && ids.length > 0 ? ids[0] : undefined);
  };

  return (
    <List>
      <MetaData />
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: '1rem',
          marginBottom: '1.5rem',
        }}
      >
        {/* Date Range */}
        <div
          style={{
            padding: '0.5rem',
            borderRadius: '2px',
            display: 'flex',
            alignItems: 'center',
            marginBottom: '2rem',
          }}
        >
          <Text style={{ fontSize: '1rem', fontWeight: 500 }}>Show data from</Text>

          <RangePicker
            value={dateRange}
            onChange={(values) => {
              if (values && values[0] && values[1]) {
                setDateRange([values[0].startOf('day'), values[1].endOf('day')]);
              }
            }}
            format="DD-MM-YYYY"
            disabledDate={disabledDate}
            getPopupContainer={() => document.body}
            dropdownClassName="custom-date-range-dropdown"
            style={{
              marginLeft: '1rem',
              border: '1px solid #d9d9d9',
              borderRadius: '4px',
            }}
            ranges={{
              'This Week': [dayjs().startOf('week'), dayjs().endOf('week')],
              'Last Month': [
                dayjs().subtract(1, 'month').startOf('month'),
                dayjs().subtract(1, 'month').endOf('month'),
              ],
              'This Month': [dayjs().startOf('month'), dayjs().endOf('month')],
              'This Year': [dayjs().startOf('year'), dayjs().endOf('year')],
            }}
          />
        </div>

        {/* Search Filters */}
        <div style={{ display: 'flex', gap: '1rem', marginTop: '0.3rem' }}>
          <div style={{ flex: 1, maxWidth: '300px' }}>
            <SearchNew
              placeholder="Search Categories"
              size="large"
              className="search"
              index={SearchIndex.Category}
              groups={['name']}
              onSelectOption={onSelectOptionCategory}
              onReset={() => setCategoryId(undefined)}
              categorySingleShow={true}
              pinnedOptions={[{ id: UNCATEGORIZED_CATEGORY_ID, label: 'Uncategorized' }]}
            />
          </div>

          <div style={{ flex: 1, maxWidth: '300px' }}>
            <SearchNew
              placeholder="Search Tags"
              size="large"
              className="search"
              index={SearchIndex.Tag}
              groups={['name']}
              onSelectOption={onSelectOption}
              onReset={() => setTagIds([])}
            />
          </div>
        </div>
      </div>

      {/* Dashboard */}
      <ReportsDashboard date={query} />

      {/* Tables */}
      <Row gutter={[32, 32]}>
        <Col lg={12} md={24}>
          <Title level={5} style={{ marginBottom: '1rem' }}>
            Purchases
          </Title>
          <ReportsTable type="purchase" date={query} />
        </Col>

        <Col lg={12} md={24}>
          <Title level={5} style={{ marginBottom: '1rem' }}>
            Sales
          </Title>
          <ReportsTable type="sales" date={query} />
        </Col>
      </Row>
    </List>
  );
};
